import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Users, CreditCard, MessageSquare, Gift, TrendingUp, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, hint, color = "emerald" }) {
  const colorMap = {
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/25",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/25",
    blue: "text-blue-300 bg-blue-500/10 border-blue-500/25",
    pink: "text-pink-300 bg-pink-500/10 border-pink-500/25",
  }[color];
  return (
    <div className={"rounded-2xl border p-4 " + colorMap}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-2 text-3xl font-black tabular-nums" dir="ltr">{value}</div>
      {hint && <div className="text-[11px] opacity-60 mt-1">{hint}</div>}
    </div>
  );
}

function DailyBars({ title, series, color = "emerald" }) {
  const max = Math.max(1, ...series.map(s => s.count));
  const barColor = { emerald: "bg-emerald-500", amber: "bg-amber-500", blue: "bg-blue-500", pink: "bg-pink-500" }[color];
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="text-xs text-zinc-400 mb-3 flex items-center justify-between">
        <span>{title}</span>
        <span dir="ltr">Σ {series.reduce((a, b) => a + b.count, 0)}</span>
      </div>
      <div className="flex items-end justify-between gap-1 h-24" dir="ltr">
        {series.map((d) => (
          <div key={d.date} className="flex flex-col items-center flex-1 min-w-0">
            <div className="w-full h-full flex items-end">
              <div
                className={"w-full rounded-t-md transition-all " + barColor}
                style={{ height: `${(d.count / max) * 100}%` }}
                title={`${d.date} → ${d.count}`}
              />
            </div>
            <div className="text-[9px] text-zinc-500 mt-1 whitespace-nowrap">{d.date.slice(5)}</div>
            <div className="text-[10px] text-zinc-200 tabular-nums">{d.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopList({ title, items }) {
  const max = Math.max(1, ...(items || []).map(i => i.count));
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="text-xs text-zinc-400 mb-3">{title}</div>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-zinc-600 py-4 text-center">no data yet</div>
      ) : (
        <div className="space-y-1.5">
          {items.slice(0, 10).map(it => (
            <div key={it.key} className="flex items-center gap-2 text-xs">
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="text-zinc-300 truncate max-w-[160px]" dir="ltr">{it.key}</span>
                <div className="flex-1 h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500/60" style={{ width: `${(it.count / max) * 100}%` }} />
                </div>
              </div>
              <div className="text-zinc-400 tabular-nums" dir="ltr">{it.count}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const { t, dir } = useI18n();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [sources, setSources] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/is-admin`).then(r => setIsAdmin(r.data.is_admin))
      .catch(() => setIsAdmin(false)).finally(() => setChecking(false));
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/analytics?days=${days}`).then(r => r.data).catch(() => null),
      axios.get(`${API}/admin/analytics/sources?days=${days}`).then(r => r.data).catch(() => null),
    ]).then(([a, s]) => { setData(a); setSources(s); setLoading(false); });
  }, [isAdmin, days]);

  if (checking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (!user) {
    return (
      <div dir={dir} className="min-h-screen flex flex-col items-center justify-center text-zinc-100 px-6">
        <ShieldAlert className="w-10 h-10 text-amber-400 mb-3" />
        <Button onClick={login} className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black">
          {t("continue_google") || "Continue with Google"}
        </Button>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div dir={dir} className="min-h-screen flex flex-col items-center justify-center text-zinc-100 px-6 text-center">
        <ShieldAlert className="w-10 h-10 text-red-400 mb-3" />
        <h1 className="text-xl font-bold">{t("admin_only") || "Admin only"}</h1>
        <Button onClick={() => navigate("/")} variant="outline" className="mt-6 rounded-full border-zinc-700">{t("back") || "Back"}</Button>
      </div>
    );
  }

  return (
    <div dir={dir} className="min-h-screen text-zinc-100 px-4 sm:px-8 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <TrendingUp className="w-6 h-6 text-emerald-400" />
          <h1 className="text-2xl sm:text-3xl font-black">{t("analytics_title") || "Analytics"}</h1>
          <div className="ms-auto flex gap-1 bg-zinc-900 rounded-full p-1">
            {[7, 14, 30, 90].map(n => (
              <button
                key={n}
                data-testid={`range-${n}`}
                onClick={() => setDays(n)}
                className={"px-3 h-8 text-xs rounded-full font-semibold transition " +
                  (days === n ? "bg-emerald-500 text-black" : "text-zinc-400 hover:text-zinc-200")}
              >
                {n} {t("days_short") || "d"}
              </button>
            ))}
          </div>
        </div>

        {loading || !data ? (
          <div className="p-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <StatCard icon={Users} label={t("stat_users") || "Total users"} value={data.totals.users}
                hint={`Google ${data.auth_providers.google || 0} · Email ${data.auth_providers.password || 0}`} color="emerald" />
              <StatCard icon={CreditCard} label={t("stat_active_subs") || "Active subs"} value={data.totals.active_subscriptions} color="amber" />
              <StatCard icon={MessageSquare} label={t("stat_feedback") || "Feedback"} value={data.totals.feedback_all} color="blue" />
              <StatCard icon={Gift} label={t("stat_referrals") || "Referrals"} value={data.totals.referrals_all} color="pink" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <DailyBars title={t("chart_new_users") || `New sign-ups (${data.days}d)`} series={data.series.new_users} color="emerald" />
              <DailyBars title={t("chart_new_subs") || `New subscriptions (${data.days}d)`} series={data.series.new_subscriptions} color="amber" />
              <DailyBars title={t("chart_new_feedback") || `Feedback received (${data.days}d)`} series={data.series.new_feedback} color="blue" />
              <DailyBars title={t("chart_new_referrals") || `Successful referrals (${data.days}d)`} series={data.series.new_referrals} color="pink" />
            </div>

            <div className="mt-6 text-[11px] text-zinc-500 text-center">
              {t("analytics_note") || "Note: these are real events from the database — sign-ups, subscriptions, feedback, referrals. Anonymous visitor tracking is not enabled yet."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
